import React, { useEffect } from 'react';
import { useLocation, useParams } from "react-router";
import { useAtomValue } from "jotai";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { getEntityType, getEntityTypeChipClassName } from '@/util/entityUtil';
import { useEntity } from "@/query/entity";
import { usePaginatedEntityValue } from "@/query/entityValue";
import { singleTestPanelOpenAtom } from "@/atom/singleTestPanel";
import { useAgentId, usePageParam, useRouteHandler } from "@/hook";
import Button from '@/component/Button';
import { SearchBar } from "@/component/input";
import PaginationBar from "@/component/PaginationBar";
import { SingleTestPanel, SingleTestButton } from '@/component/singleTest';
import { BackIcon } from '@/assets/svgs/icons';

import EntityValueTable from "./component/EntityValueTable";
import EntityValueForm from "./component/EntityValueForm";


const ENTITY_VALUE_PAGE_SIZE = 15;

export interface EntityValuePageParam {
    page: number;
    size: number;
    searchKeyword: string;
}

const EntityValueView: React.FC = () => {
    const intl = useIntl();
    const location = useLocation();
    const agentId = useAgentId();
    const { entityId } = useParams() as {entityId: string};
    const { moveToEntityValue, moveToEntity } = useRouteHandler();

    const { param, set: setParam } = usePageParam<EntityValuePageParam>({
        page: 0,
        size: ENTITY_VALUE_PAGE_SIZE,
        searchKeyword: "",
    });

    const singleTestPanelOpen = useAtomValue(singleTestPanelOpenAtom);
    const { data: entity } = useEntity(agentId, entityId);
    const { data: entityValuePage } = usePaginatedEntityValue(
        agentId,
        entityId,
        param?.page,
        param?.size,
        param?.searchKeyword,
    );

    const entityType = entity ? getEntityType(entity) : "";

    useEffect(() => {
        if (location.state?.searchQuery) {
            setParam({
                searchKeyword: location.state.searchQuery,
            });
        }
        moveToEntityValue(agentId, entityId, { replace: true, state: null });
        // eslint-disable-next-line
    }, [location.state?.searchQuery]);

    if (!entity) {
        return null;
    }
    return (
        <div className={classNames(
                "agent-content-layout",
                singleTestPanelOpen && "test-panel-open",
             )}
        >
            <div className={classNames(
                    "full-page-container flex flex-col gap-1.5",
                    singleTestPanelOpen && "test-panel-open",
                )}
            >
                <div className="search-bar-container">
                    <SearchBar lg width={680}
                            placeholder={intl.formatMessage({ id: "i00070" })}
                            defaultValue={param?.searchKeyword}
                            onSearch={query => setParam({ searchKeyword: query })}
                    />
                </div>

                <div className="subtitle-container">
                    <div className="w-left-editor-section flex items-center gap-6">
                        <div className="subtitle-container-title">
                            {entity.name}
                        </div>

                        <div className={classNames(entityType && getEntityTypeChipClassName(entityType))}>
                            {entityType.toUpperCase()}
                        </div>
                    </div>
                    <div className="subtitle-container-btn-group">
                        <Button variant="text"
                                onClick={() => moveToEntity(agentId)}
                        >
                            <BackIcon className="size-3.5 mb-0.5" />
                            {intl.formatMessage({ id: "i05051" })}
                        </Button>

                        <SingleTestButton />
                    </div>
                </div>

                <div className="form-table-container">
                    <EntityValueForm entityId={entityId} />

                    <div className="table-pagination-container">
                        <EntityValueTable entityId={entityId}
                                        page={entityValuePage}
                                        pageParam={param}
                        />
                        <PaginationBar pageData={entityValuePage}
                                    onClickPage={page => setParam({ page: page })}
                        />
                    </div>

                    <SingleTestPanel className="ml-auto" />
                </div>
            </div>
        </div>
    );
};

export default EntityValueView;